import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Euro, BookCheck, Percent, Wallet, Moon, CalendarClock, Loader2, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useActiveProperty, useAnalytics, type AnalyticsPeriod, type BookingStatus, type Channel } from '../../hooks/useManagement';
import PropertySelector from '../../components/PropertySelector';

const eur = (n: number) => `€${Math.round(n).toLocaleString('es-ES')}`;
const MONTHS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'] as const;

// 'YYYY-MM' → 'Mmm YY' (short, locale-friendly without a date lib dependency here).
const fmtMonth = (key: string) => {
  const [y, m] = key.split('-');
  const idx = Math.max(0, Math.min(11, Number(m) - 1));
  return `${MONTHS_ES[idx]} ${y?.slice(2) ?? ''}`;
};

const statusStyles: Record<BookingStatus, { bar: string; chip: string }> = {
  confirmed: { bar: 'bg-emerald-500', chip: 'bg-emerald-100 text-emerald-800' },
  pending: { bar: 'bg-amber-500', chip: 'bg-amber-100 text-amber-800' },
  completed: { bar: 'bg-slate-400', chip: 'bg-slate-100 text-slate-700' },
  cancelled: { bar: 'bg-rose-500', chip: 'bg-rose-100 text-rose-800' },
};

const channelColors: Record<Channel, string> = {
  direct: 'bg-teal-500',
  airbnb: 'bg-rose-500',
  booking: 'bg-blue-500',
  vrbo: 'bg-violet-500',
};

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const { propertyId, properties } = useActiveProperty();
  const hasProperties = properties.length > 0;

  const periods: { key: AnalyticsPeriod; label: string }[] = [
    { key: '30d', label: t('portal.manage.analytics.period30d', '30 días') },
    { key: '90d', label: t('portal.manage.analytics.period90d', '90 días') },
    { key: '12m', label: t('portal.manage.analytics.period12m', '12 meses') },
    { key: 'all', label: t('portal.manage.analytics.periodAll', 'Todo') },
  ];

  const [period, setPeriod] = useState<AnalyticsPeriod>('90d');

  const { data, isLoading } = useAnalytics(propertyId, period);

  const statusLabels: Record<BookingStatus, string> = {
    pending: t('portal.manage.status.pending'),
    confirmed: t('portal.manage.status.confirmed'),
    cancelled: t('portal.manage.status.cancelled'),
    completed: t('portal.manage.status.completed'),
  };
  const channelLabels: Record<Channel, string> = {
    direct: t('portal.manage.channel.direct'),
    airbnb: t('portal.manage.channel.airbnb'),
    booking: t('portal.manage.channel.booking'),
    vrbo: t('portal.manage.channel.vrbo'),
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('portal.manage.analytics.title', 'Estadísticas')}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {t('portal.manage.analytics.subtitle', 'Rendimiento de tus reservas')}
          </p>
        </div>
        <PropertySelector allowAll />
      </div>

      {!hasProperties ? (
        <EmptyState
          title={t('portal.manage.analytics.noPropertyTitle', 'Aún no hay datos')}
          sub={t('portal.manage.selector.noPropertyHint', 'Primero crea un alojamiento para gestionar sus reservas.')}
        />
      ) : (
        <>
          {/* Period toggle */}
          <div className="inline-flex flex-wrap items-center gap-1 rounded-xl bg-slate-100 p-1">
            {periods.map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPeriod(p.key)}
                className={`rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors ${
                  period === p.key ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {isLoading || !data ? (
            <div className="flex justify-center py-20">
              <Loader2 className="animate-spin text-teal-500" size={32} />
            </div>
          ) : data.kpis.totalBookings === 0 ? (
            <EmptyState
              title={t('portal.manage.analytics.emptyTitle', 'Sin reservas en este periodo')}
              sub={t('portal.manage.analytics.emptySub', 'Cuando empieces a recibir reservas, aquí verás tus estadísticas.')}
            />
          ) : (
            <>
              {/* KPI cards */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <KpiCard
                  label={t('portal.manage.analytics.kpiRevenue', 'Ingresos confirmados')}
                  value={eur(data.kpis.revenue)}
                  sub={t('portal.manage.analytics.kpiCollected', { amount: eur(data.kpis.collected), defaultValue: 'Cobrado: {{amount}}' })}
                  icon={Euro} tone="teal"
                />
                <KpiCard
                  label={t('portal.manage.analytics.kpiBookings', 'Reservas')}
                  value={String(data.kpis.totalBookings)}
                  sub={t('portal.manage.analytics.kpiConfirmed', { count: data.kpis.confirmed, defaultValue: '{{count}} confirmadas' })}
                  icon={BookCheck} tone="emerald"
                />
                <KpiCard
                  label={t('portal.manage.analytics.kpiOccupancy', 'Ocupación')}
                  value={data.kpis.occupancy != null ? `${data.kpis.occupancy}%` : '—'}
                  sub={t('portal.manage.analytics.kpiOccupancySub', 'Aprox. del periodo')}
                  icon={Percent} tone="teal"
                />
                <KpiCard
                  label={t('portal.manage.analytics.kpiAvgValue', 'Valor medio')}
                  value={eur(data.kpis.avgBookingValue)}
                  sub={t('portal.manage.analytics.kpiAvgStay', { nights: data.kpis.avgStayLength, defaultValue: '{{nights}} noches/estancia' })}
                  icon={Wallet} tone="slate"
                />
                <KpiCard
                  label={t('portal.manage.analytics.kpiNights', 'Noches reservadas')}
                  value={String(data.kpis.nights)}
                  sub={t('portal.manage.analytics.kpiLeadTime', { days: data.kpis.avgLeadTime, defaultValue: '{{days}} días de antelación' })}
                  icon={Moon} tone="emerald"
                />
                <KpiCard
                  label={t('portal.manage.analytics.kpiUpcoming', 'Próximas entradas')}
                  value={String(data.kpis.upcoming)}
                  sub={t('portal.manage.analytics.kpiUpcomingSub', 'En los próximos 30 días')}
                  icon={CalendarClock} tone={data.kpis.upcoming > 0 ? 'teal' : 'slate'}
                />
              </div>

              {/* Revenue by month bar chart */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">{t('portal.manage.analytics.revenueByMonth', 'Ingresos por mes')}</h3>
                  <span className="text-xs text-slate-400">{t('portal.manage.analytics.last12m', 'Últimos 12 meses')}</span>
                </div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={data.revenueByMonth.map(r => ({ ...r, label: fmtMonth(r.month) }))}
                    margin={{ top: 5, right: 5, left: -15, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={48} />
                    <Tooltip
                      cursor={{ fill: '#f8fafc' }}
                      formatter={(v: number, _n, p) => [
                        `${eur(v)} · ${t('portal.manage.analytics.bookingsCount', { count: (p?.payload?.bookings ?? 0) as number, defaultValue: '{{count}} reservas' })}`,
                        t('portal.manage.analytics.revenueTooltip', 'Ingresos'),
                      ]}
                      contentStyle={{ border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }}
                    />
                    <Bar dataKey="revenue" fill="#14b8a6" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Status breakdown + channel mix */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Bookings by status */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">{t('portal.manage.analytics.byStatus', 'Reservas por estado')}</h3>
                  <div className="space-y-3">
                    {(Object.keys(data.byStatus) as BookingStatus[]).map(s => {
                      const count = data.byStatus[s];
                      const pct = data.kpis.totalBookings > 0 ? (count / data.kpis.totalBookings) * 100 : 0;
                      return (
                        <div key={s}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusStyles[s].chip}`}>
                              {statusLabels[s]}
                            </span>
                            <span className="font-semibold text-slate-700">{count}</span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className={`h-full rounded-full ${statusStyles[s].bar}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Channel mix */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">{t('portal.manage.analytics.channelMix', 'Canales de reserva')}</h3>
                  {data.channelMix.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-6">{t('portal.manage.analytics.noChannels', 'Sin datos de canales todavía.')}</p>
                  ) : (
                    <div className="space-y-3">
                      {data.channelMix.map(c => {
                        const pct = data.kpis.totalBookings > 0 ? (c.count / data.kpis.totalBookings) * 100 : 0;
                        return (
                          <div key={c.channel}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="font-medium text-slate-700">{channelLabels[c.channel] ?? c.channel}</span>
                              <span className="text-slate-500">
                                {c.count} · {eur(c.revenue)}
                              </span>
                            </div>
                            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                              <div className={`h-full rounded-full ${channelColors[c.channel] ?? 'bg-slate-400'}`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Nights per property (only when no property filter) */}
              {data.nightsByProperty.length > 1 && (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">{t('portal.manage.analytics.nightsByProperty', 'Noches por alojamiento')}</h3>
                  <div className="space-y-3">
                    {(() => {
                      const maxNights = Math.max(1, ...data.nightsByProperty.map(p => p.nights));
                      return data.nightsByProperty.map(p => (
                        <div key={p.property_id ?? 'none'}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="font-medium text-slate-700 truncate max-w-[16rem]">{p.name}</span>
                            <span className="text-slate-500">{t('portal.manage.analytics.nightsValue', { count: p.nights, defaultValue: '{{count}} noches' })}</span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full bg-teal-500" style={{ width: `${(p.nights / maxNights) * 100}%` }} />
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function EmptyState({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-12 flex flex-col items-center text-center">
      <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center mb-4">
        <BarChart3 className="w-6 h-6" />
      </div>
      <p className="text-slate-900 font-medium">{title}</p>
      <p className="text-sm text-slate-500 mt-1 max-w-sm">{sub}</p>
    </div>
  );
}

function KpiCard({
  label, value, sub, icon: Icon, tone,
}: {
  label: string;
  value: string;
  sub: string;
  icon: React.ElementType;
  tone: 'teal' | 'emerald' | 'amber' | 'slate';
}) {
  const tones: Record<typeof tone, string> = {
    teal: 'bg-teal-50 text-teal-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    slate: 'bg-slate-100 text-slate-600',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-slate-500">{label}</span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-400 mt-1">{sub}</div>
    </div>
  );
}
